import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth/useAuth';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string | null;
  timezone: string;
}

export function useUserProfile() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  return useQuery<UserProfile | null>({
    queryKey: ['user_profile', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, email, full_name, timezone')
        .eq('id', userId!)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

/**
 * Backfill the user's timezone from the browser when it isn't set yet, so
 * event-reminder emails render in their local zone instead of the server
 * fallback. Only fills an empty value — an explicit choice in Settings wins.
 */
export function useEnsureTimezone(): void {
  const { data: profile } = useUserProfile();
  const update = useUpdateUserProfile();
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !profile) return;
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (detected && !profile.timezone?.trim()) {
      done.current = true;
      void update.mutateAsync({ timezone: detected });
    }
  }, [profile, update]);
}

export function useUpdateUserProfile() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Pick<UserProfile, 'timezone' | 'full_name'>>) => {
      if (!userId) throw new Error('not_authenticated');
      const { error } = await supabase
        .from('users')
        .update(patch as never)
        .eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user_profile', userId] }),
  });
}
